import { fetchConfig } from '@theme/utilities';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

/**
 * "Clear all" in the cart drawer header (23/09/2026): empties the cart in one request and lets the
 * theme repaint the drawer from the section that comes back, like st-cart-variant.js does.
 *
 * Emptying a cart cannot be undone, so the first click only arms the button ("Confirm clear?" in the
 * accent colour) and the second one within CONFIRM_MS does the work; clicking anything else or
 * letting the timer run out puts the label back.
 *
 * The button is inside the morphed section, so the listener is delegated on the document and
 * registered only once.
 */
if (!window.__stCartClear) {
  window.__stCartClear = true;

  // Playbook §6.3: count every cart change so a late response never repaints over a newer state.
  let cartMutationSeq = 0;
  document.addEventListener(ThemeEvents.cartUpdate, () => {
    cartMutationSeq++;
  });

  let busy = false;

  const CONFIRM_MS = 4000;
  /** @type {{ button: Element, label: string, timer: number } | null} */
  let armed = null;

  const disarm = () => {
    if (!armed) return;
    clearTimeout(armed.timer);
    armed.button.textContent = armed.label;
    armed.button.classList.remove('is-confirming');
    armed = null;
  };

  document.addEventListener(ThemeEvents.cartUpdate, disarm);

  document.addEventListener('click', async (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('[data-st-clear-cart]');
    if (!button) {
      disarm();
      return;
    }
    if (busy) return;

    event.preventDefault();

    // first click arms, second one clears
    if (!armed || armed.button !== button) {
      disarm();
      const label = button.textContent?.trim() || 'Clear all';
      button.textContent = 'Confirm clear?';
      button.classList.add('is-confirming');
      armed = { button, label, timer: setTimeout(disarm, CONFIRM_MS) };
      return;
    }
    clearTimeout(armed.timer);
    const restore = armed;
    armed = null;
    const host = button.closest('cart-items-component') || document.querySelector('cart-items-component[data-drawer]');
    const sectionId = host instanceof HTMLElement ? host.dataset.sectionId || '' : '';

    busy = true;
    button.setAttribute('disabled', '');
    host?.classList.add('cart-items-disabled');

    try {
      const body = JSON.stringify(sectionId ? { sections: [sectionId], sections_url: window.location.pathname } : {});
      const seqAtStart = cartMutationSeq;
      const response = await fetch(`${Theme.routes.cart_url}/clear.js`, fetchConfig('json', { body }));
      const data = await response.json();
      if (!response.ok) throw new Error(data?.description || data?.message || 'Cart clear failed');

      // a newer cart change started meanwhile: announce the cart, but let that change paint the drawer
      if (cartMutationSeq !== seqAtStart) data.sections = undefined;

      button.dispatchEvent(
        new CartUpdateEvent(data, sectionId || 'st-cart-clear', {
          source: 'st-cart-clear',
          itemCount: 0,
          sections: data.sections,
        })
      );
    } catch (error) {
      restore.button.textContent = restore.label;
      restore.button.classList.remove('is-confirming');
      console.warn('[st-cart-clear]', error);
    } finally {
      busy = false;
      button.removeAttribute('disabled');
      host?.classList.remove('cart-items-disabled');
    }
  });
}
